import { useState } from 'react';
import { BoundedContent, Box, H2, P, Section } from '@vp/swan';
import { scrollToElementById } from '@99designs/browser';
import { BIFROST_ENABLED_LOCALES } from '@99designs/design-services-common';
import { useInVistaExperimentVariant, useIsFeatureFlagActive } from '@99designs/experiments';
import { __ } from '@99designs/i18n';
import { Recommendations } from '@99designs/recommendations';
import { TrustpilotWidget, isTrustpilotEnabledLocale } from '@99designs/reviews';
import { usePage } from '@99designs/tracking';
import { getNavigationData } from '../../../lib/tracking';
import { DesignCategories } from '../../DesignCategories';
import { FAQ } from '../../FAQ';
import { HowItWorks } from '../../HowItWorks';
import { LogoBanner } from '../../LogoBanner';
import { StickyNav } from '../../StickyNav';
import { SupportBanner } from '../../SupportBanner';
import { WhyHireOnVistaprint } from '../../WhyHireOnVistaprint';
import { Header } from '../Header';
import { ProductCarousel } from '../ProductCarousel';
import type { SupportedLocale } from '@99designs/i18n';
import type { PopularProducts as PopularProductsType, ProductGroups } from '../../../types';

interface HolidayHubMainContentProps {
    selectedCategoryId?: string;
    locale: SupportedLocale;
    productGroups: ProductGroups;
    popularProducts?: PopularProductsType;
}

const RecommendationsSectionId = 'recommendations';
const HolidaySectionId = 'holiday-section';
const DesignCategoriesSectionId = 'design-categories-section';
const WhyHireOnVistaprintSectionId = 'why-hire-on-vistaprint-section';
const HowItWorksSectionId = 'how-it-works-section';
const FAQSectionId = 'faq-section';
const ScrollTopMargin = 75;

export const HolidayHubMainContent = ({
    selectedCategoryId,
    locale,
    productGroups,
    popularProducts,
}: HolidayHubMainContentProps) => {
    const { pageData } = usePage();

    const inVariant = useInVistaExperimentVariant('design_services_hub_page_recently_viewed_rec');
    const [error, setError] = useState('');
    const showRecommendations = locale === 'en-US' && inVariant('test');
    const showRecommendationsNavItem = showRecommendations && error === '';
    const recommendationsNavItem = {
        label: __('Recommended', {
            _context: 'Navigation link to the section displaying recommended design services',
        }),
        href: `#recommendations`,
    };

    let bannerBelowHowItWorks = null;
    if (BIFROST_ENABLED_LOCALES.includes(locale)) {
        bannerBelowHowItWorks = (
            <Section marginBottom="between-sections" style={{ boxShadow: 'none' }}>
                <LogoBanner locale={locale} />
            </Section>
        );
    }
    const isTrustpilotFeatureEnabled = useIsFeatureFlagActive(
        'design_services_hub_page_trust_signal'
    );

    return (
        <>
            <Section>
                <Header
                    browseDesignServicesCtaOnClick={() => {
                        scrollToElementById(HolidaySectionId, ScrollTopMargin);
                    }}
                    howItWorksCtaOnClick={() => {
                        scrollToElementById(HowItWorksSectionId, ScrollTopMargin);
                    }}
                />
            </Section>
            <StickyNav
                navItems={[
                    ...(showRecommendationsNavItem ? [recommendationsNavItem] : []),
                    { label: __('Holiday favorites'), href: `#${HolidaySectionId}` },
                    { label: __('Design services'), href: `#${DesignCategoriesSectionId}` },
                    { label: __('Why Vistaprint?'), href: `#${WhyHireOnVistaprintSectionId}` },
                    { label: __('How it works'), href: `#${HowItWorksSectionId}` },
                    { label: __('FAQs'), href: `#${FAQSectionId}` },
                ]}
            />
            {showRecommendations && (
                <Section
                    marginTop="between-sections"
                    marginBottom="between-sections"
                    id={RecommendationsSectionId}
                    style={{ boxShadow: 'none' }}
                >
                    <Recommendations onError={setError} />
                </Section>
            )}
            {popularProducts && popularProducts.length > 0 ? (
                <Section
                    marginTop="between-sections"
                    marginBottom="between-sections"
                    id={HolidaySectionId}
                    style={{ boxShadow: 'none' }}
                >
                    <BoundedContent>
                        <Box paddingLeft={{ xs: 3, md: 0 }} paddingRight={{ xs: 3, md: 0 }}>
                            <H2 marginBottom="6">{__('Holiday favorites')}</H2>
                            <ProductCarousel
                                products={popularProducts}
                                locale={locale}
                                productClickedTrackingData={(product, index) => {
                                    return getNavigationData(product, {
                                        pageName: pageData.pageName,
                                        pageStage: pageData.pageStage,
                                        pageSection: pageData.pageSection,
                                        pagePath: pageData.pagePath ?? window.location.pathname,
                                        pageZone: 'Holiday Tiles',
                                        position: index,
                                        isHolidayHub: true,
                                    });
                                }}
                                maxSlides={6}
                            />
                        </Box>
                    </BoundedContent>
                </Section>
            ) : (
                <Section
                    marginTop="between-sections"
                    marginBottom="between-sections"
                    id={HolidaySectionId}
                    style={{ boxShadow: 'none' }}
                >
                    <P textAlign="center">
                        {__(
                            'Whoops - design categories could not be loaded but we are working on it.',
                            {
                                _context:
                                    'An error message displayed on a landing page when a component containing design categories cannot be loaded correctly',
                            }
                        )}
                    </P>
                </Section>
            )}
            {productGroups.length > 0 ? (
                <Section
                    marginTop="between-sections"
                    marginBottom="between-sections"
                    id={DesignCategoriesSectionId}
                    style={{ boxShadow: 'none' }}
                >
                    <DesignCategories
                        title={__('Design Services')}
                        categories={productGroups}
                        locale={locale as SupportedLocale}
                        initialCategoryId={selectedCategoryId}
                    />
                </Section>
            ) : (
                <Section
                    marginTop="between-sections"
                    marginBottom="between-sections"
                    id={DesignCategoriesSectionId}
                    style={{ boxShadow: 'none' }}
                >
                    <P textAlign="center">
                        {__(
                            'Whoops - design categories could not be loaded but we are working on it.',
                            {
                                _context:
                                    'An error message displayed on a landing page when a component containing design categories cannot be loaded correctly',
                            }
                        )}
                    </P>
                </Section>
            )}
            <Section
                marginBottom="between-sections"
                id={WhyHireOnVistaprintSectionId}
                style={{ boxShadow: 'none' }}
            >
                <WhyHireOnVistaprint />
            </Section>
            <Section
                marginBottom="between-sections"
                id={HowItWorksSectionId}
                style={{ boxShadow: 'none' }}
            >
                <HowItWorks locale={locale} />
            </Section>

            {bannerBelowHowItWorks}

            {isTrustpilotEnabledLocale(locale) && isTrustpilotFeatureEnabled() && (
                <Section marginBottom="between-sections" style={{ boxShadow: 'none' }}>
                    <TrustpilotWidget locale={locale} />
                </Section>
            )}

            <Section
                marginBottom="between-sections"
                id={FAQSectionId}
                style={{ boxShadow: 'none' }}
            >
                <FAQ />
            </Section>
            <Section style={{ boxShadow: 'none' }}>
                <SupportBanner locale={locale} />
            </Section>
        </>
    );
};
